import React from 'react';
import * as Yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  SparkForm,
  SparkTextField,
  SparkFormButton,
  SparkPasswordField,
} from 'sharedComponents/SparkForm';
import { useRegisterUser } from 'hooks/auth/useRegisterUser';
import { useUserStore } from 'stores';
import { PASSWORD_PATTERN } from 'constants/regex';

type RegisterFormType = {
  surname: string;
  name: string;
  email: string;
  password: string;
};

const initialValues = {
  surname: '',
  name: '',
  email: '',
  password: '',
};
const validationSchema = Yup.object({
  surname: Yup.string().required('Surname is Required'),
  name: Yup.string().required('Name is Required'),
  email: Yup.string()
    .required('Email is Required')
    .email('Please enter a valid Email'),
  password: Yup.string()
    .required(
      'Passwords must be at least 6 characters. One lowercase, one uppercase, one digit, and one special character.'
    )
    .min(6, 'Passwords must be at least 6 characters.')
    .matches(
      PASSWORD_PATTERN,
      'Passwords must have at least one lowercase, one uppercase, one digit, and one special character.'
    ),
});
type RegisterFormProps = {
  modalView?: boolean;
  showLogo?: boolean;
  callBack?: () => void;
};
function RegisterForm({
  modalView,
  showLogo = true,
  callBack,
}: RegisterFormProps) {
  const setApplicationUser = useUserStore((state) => state.setApplicationUser);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get('ref_code');
  const subscriptionCreditOptionID = searchParams.get('sco_id');

  const { mutateAsync: registerUser, isLoading } = useRegisterUser();

  const handleRegister = async (formValues: RegisterFormType) => {
    const { surname, name, email, password } = formValues;
    const newUserResponse = await registerUser({
      surname,
      name,
      email,
      password,
      referralCode,
      subscriptionCreditOptionID,
    });
    setApplicationUser(newUserResponse.data);
    if (callBack) {
      callBack();
    } else {
      navigate('/onboarding');
    }
  };

  return (
    <SparkForm<RegisterFormType>
      initialValues={initialValues}
      onSave={handleRegister}
      validationSchema={validationSchema}
    >
      <SparkTextField
        name='name'
        label='Name'
        size={12}
        placeholder='Enter Name'
      />
      <SparkTextField
        name='surname'
        label='Surname'
        size={12}
        placeholder='Enter Surname'
      />
      <SparkTextField
        name='email'
        label='Email'
        placeholder='Enter Email'
        size={12}
        autoComplete='off'
        autoLowerCase={true}
      />

      <SparkPasswordField
        name='password'
        label='Password'
        placeholder='Enter Password'
        renderForgetPasswordLink={false}
        size={12}
        modalView={modalView}
        showLogo={showLogo}
      />
      <SparkFormButton
        sx={{
          padding: 2,
          textDecoration: 'none',
          fontSize: '14px',
        }}
        disabled={isLoading}
      >
        CONTINUE
      </SparkFormButton>
    </SparkForm>
  );
}

export default RegisterForm;
